import { useState } from 'react';

import { FUNNEL_COLORS, FUNNEL_LABELS } from '../../../core/constants/kpis';
import { fmtNum, fmtPct } from '../utils/format';

// Embudo 3D (SVG): cada etapa es una banda de paredes rectas con bordes superior
// e inferior CURVOS (arcos elípticos → profundidad), levemente separadas. Solo la
// primera etapa lleva el disco/boca completo (la abertura del embudo). El ancho
// usa el `display` (0–100, ponderado del API); el `total` real va en la leyenda.
//
// Dos variantes (tab): COMPACTA (Viables → Transferidos → Preñeces → Partos, el
// resumen reproductivo) y COMPLETA (todas las etapas del API). En compacta se
// recalculan `display` y `rate_vs_previous` sobre el subconjunto (la boca = Viables).

const COMPACT_KEYS = ['viables', 'transferidos', 'preneces', 'partos'];

function toCompact(stages) {
    const picked = COMPACT_KEYS.map((k) => stages.find((s) => s.key === k)).filter(Boolean);
    const first = Number(picked[0]?.total) || 0;
    return picked.map((s, i) => {
        const total = Number(s.total) || 0;
        const prev = i > 0 ? (Number(picked[i - 1].total) || 0) : null;
        return {
            ...s,
            display: first > 0 ? Math.max(0, Math.min(100, (total / first) * 100)) : 0,
            rate_vs_previous: i === 0 ? null : (prev > 0 ? (total / prev) * 100 : null),
        };
    });
}

const W = 280;
const CX = W / 2;
const RY = 8;        // radio vertical de los arcos = curvatura/profundidad
const BAND_H = 38;   // alto por etapa
const GAP = 5;       // separación leve entre etapas

// Oscurece (f<0) un color #hex para el borde de la boca.
function shade(hex, f) {
    if (typeof hex !== 'string' || hex[0] !== '#') return hex;
    const n = parseInt(hex.slice(1), 16);
    const clamp = (c) => Math.max(0, Math.min(255, Math.round(c)));
    let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    if (f >= 0) { r += (255 - r) * f; g += (255 - g) * f; b += (255 - b) * f; }
    else { r *= 1 + f; g *= 1 + f; b *= 1 + f; }
    return `rgb(${clamp(r)}, ${clamp(g)}, ${clamp(b)})`;
}

// Banda con bordes curvos (arcos elípticos que bulgan hacia abajo) y paredes rectas.
function bandPath(topR, botR, y0, y1) {
    const tl = CX - topR, tr = CX + topR, bl = CX - botR, br = CX + botR;
    return `M ${tl} ${y0} A ${topR} ${RY} 0 0 0 ${tr} ${y0} L ${br} ${y1} A ${botR} ${RY} 0 0 1 ${bl} ${y1} Z`;
}

export default function FunnelChart({ funnel }) {
    const [variant, setVariant] = useState('full'); // 'full' | 'compact'
    const raw = funnel?.stages ?? [];
    const stages = variant === 'compact' ? toCompact(raw) : raw;
    const n = stages.length;
    const rOf = (d) => (Math.max(0, Math.min(100, Number(d) || 0)) / 100) * W / 2;

    const segs = [];
    let y = RY;
    stages.forEach((s, i) => {
        const next = stages[i + 1];
        const topR = rOf(s.display);
        const botR = next ? rOf(next.display) : topR;
        segs.push({ ...s, topR, botR, y0: y, y1: y + BAND_H });
        y += BAND_H + GAP;
    });
    const height = n ? (segs[n - 1].y1 + RY) : 0;
    const first = segs[0];
    const firstColor = first && (FUNNEL_COLORS[first.key] || '#04c2a0');

    return (
        <article className="report-card report-funnel">
            <div className="report-funnel__head">
                <h2 className="report-section__title">Embudo reproductivo</h2>
                <div className="report-funnel__tabs" role="tablist" aria-label="Variante del embudo">
                    <button type="button" role="tab" aria-selected={variant === 'compact'} className={`report-funnel__tab ${variant === 'compact' ? 'report-funnel__tab--active' : ''}`} onClick={() => setVariant('compact')}>Compacto</button>
                    <button type="button" role="tab" aria-selected={variant === 'full'} className={`report-funnel__tab ${variant === 'full' ? 'report-funnel__tab--active' : ''}`} onClick={() => setVariant('full')}>Completo</button>
                </div>
            </div>

            {!n ? (
                <p className="report-empty">Sin datos en el período.</p>
            ) : (
                <div className="report-funnel__body">
                    <svg className="report-funnel__chart" viewBox={`0 0 ${W} ${height}`} role="img" aria-label="Embudo reproductivo">
                        {/* boca: disco completo solo en la etapa superior (la abertura) */}
                        <ellipse cx={CX} cy={first.y0} rx={first.topR} ry={RY} fill={shade(firstColor, -0.14)} />
                        {segs.map((s) => (
                            <path key={s.key} d={bandPath(s.topR, s.botR, s.y0, s.y1)} fill={FUNNEL_COLORS[s.key] || 'var(--color-primary)'} />
                        ))}
                    </svg>

                    <ul className="report-funnel__legend">
                        {stages.map((s) => (
                            <li key={s.key} className="report-funnel__chip">
                                <span className="report-funnel__dot" style={{ background: FUNNEL_COLORS[s.key] || 'var(--color-primary)' }} />
                                <span className="report-funnel__name">{FUNNEL_LABELS[s.key] || s.key}</span>
                                <strong className="report-funnel__value">{fmtNum(s.total)}</strong>
                                {s.rate_vs_previous != null && (
                                    <span className="report-funnel__delta" title="Conversión vs. la etapa previa">{fmtPct(s.rate_vs_previous)}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </article>
    );
}
